import { Routes } from '@angular/router';
import { AuthGuard, LoginGuard } from './auth/guards/auth.guard';
import { RoleGuard, AdminGuard } from './auth/guards/role.guard';
import { RouteManagementComponent } from './features/route-management/route-management.component';
import { CreateRouteComponent } from './features/route-management/create-route/create-route.component';
import { inject } from '@angular/core';
import { RoleAccessService } from './auth/services/role-access.service';
import { Router } from '@angular/router';
import { getFeaturePath } from './auth/feature-constants';
import { BillingManagementComponent } from './features/billing-management/billing-management.component';
import { TripSummaryComponent } from './features/billing-management/trip-summary/trip-summary.component';
import { BillGenerationComponent } from './features/billing-management/bill-generation/bill-generation.component';
import { GeneratedBillComponent } from './features/billing-management/generated-bill/generated-bill.component';
import { Feature } from './auth/models/feature.model';

// Resolver function to redirect to first available feature
const redirectToFirstAvailableFeature = () => {
  const roleAccessService = inject(RoleAccessService);
  const router = inject(Router);

  const allowedFeatures = roleAccessService.getUserAllowedFeatures();

  if (allowedFeatures.length > 0) {
    // Try dashboard first, then first available feature
    if (allowedFeatures.includes(Feature.DASHBOARD)) {
      router.navigate(['/dashboard']);
    } else {
                const firstFeature = allowedFeatures[0];
          const featurePath = getFeaturePath(String(firstFeature));
      if (featurePath) {
        router.navigate([featurePath]);
      } else {
        router.navigate(['/no-access']);
      }
    }
  } else {
    router.navigate(['/no-access']);
  }

  return false;
};

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./auth/components/login/login.component').then((m) => m.LoginComponent),
    canActivate: [LoginGuard],
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(
        (m) => m.DashboardComponent
      ),
    canActivate: [RoleGuard(Feature.DASHBOARD)],
  },
  {
    path: 'tripManagement',
    // canActivate: [RoleGuard(Feature.TRIP)],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/trip-management/trip-management.component').then(
            (m) => m.TripManagementComponent
          ),
        canActivate: [RoleGuard(Feature.TRIP)],
      },
      {
        path: 'create',
        loadComponent: () =>
          import(
            './features/trip-management/create-trip/create-trip.component'
          ).then((m) => m.CreateTripComponent),
        canActivate: [RoleGuard(Feature.TRIP)],
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import(
            './features/trip-management/create-trip/create-trip.component'
          ).then((m) => m.CreateTripComponent),
        canActivate: [RoleGuard(Feature.TRIP)],
      },
      {
        path: 'schedule',
        loadComponent: () =>
          import('./features/schedule-trip/schedule-trip.component').then(
            (m) => m.ScheduleTripComponent
          ),
        canActivate: [RoleGuard(Feature.SCHEDULE_TRIP)],
      },
      {
        path: 'schedule/create',
        loadComponent: () =>
          import(
            './features/schedule-trip/create-trip/create-trip.component'
          ).then((m) => m.CreateTripComponent),
        canActivate: [RoleGuard(Feature.SCHEDULE_TRIP)],
      },
      {
        path: 'schedule/edit/:id',
        loadComponent: () =>
          import(
            './features/schedule-trip/create-trip/create-trip.component'
          ).then((m) => m.CreateTripComponent),
        canActivate: [RoleGuard(Feature.SCHEDULE_TRIP)],
      },
    ],
  },
  {
    path: 'VenderManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/vender-management/vender-management.component'
          ).then((m) => m.VenderManagementComponent),
        canActivate: [RoleGuard(Feature.VENDOR_MANAGEMENT)],
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import(
            './features/vender-management/vendor-onboard/vendor-onboard.component'
          ).then((m) => m.VendorOnboardComponent),
        canActivate: [RoleGuard(Feature.VENDOR_MANAGEMENT)],
      },
      {
        path: 'onboard/:id',
        loadComponent: () =>
          import(
            './features/vender-management/vendor-onboard/vendor-onboard.component'
          ).then((m) => m.VendorOnboardComponent),
        canActivate: [RoleGuard(Feature.VENDOR_MANAGEMENT)],
      },
    ],
  },
  {
    path: 'VehicleManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/vehicle-management/vehicle-management.component'
          ).then((m) => m.VehicleManagementComponent),
        canActivate: [RoleGuard(Feature.VEHICLE_MANAGEMENT)],
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import(
            './features/vehicle-management/vehicle-onboard/vehicle-onboard.component'
          ).then((m) => m.VehicleOnboardComponent),
        canActivate: [RoleGuard(Feature.VEHICLE_MANAGEMENT)],
      },
      {
        path: 'onboard/:id',
        loadComponent: () =>
          import(
            './features/vehicle-management/vehicle-onboard/vehicle-onboard.component'
          ).then((m) => m.VehicleOnboardComponent),
        canActivate: [RoleGuard(Feature.VEHICLE_MANAGEMENT)],
      },
    ],
  },
  {
    path: 'DriverManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/driver-management/driver-management.component'
          ).then((m) => m.DriverManagementComponent),
        canActivate: [RoleGuard(Feature.DRIVER_MANAGEMENT)],
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import(
            './features/driver-management/driver-onboard/driver-onboard.component'
          ).then((m) => m.DriverOnboardComponent),
        canActivate: [RoleGuard(Feature.DRIVER_MANAGEMENT)],
      },
      {
        path: 'onboard/:id',
        loadComponent: () =>
          import(
            './features/driver-management/driver-onboard/driver-onboard.component'
          ).then((m) => m.DriverOnboardComponent),
        canActivate: [RoleGuard(Feature.DRIVER_MANAGEMENT)],
      },
    ],
  },
  {
    path: 'RouteManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/route-management/route-management.component').then(
            (m) => m.RouteManagementComponent
          ),
        canActivate: [RoleGuard(Feature.ROUTE_MANAGEMENT)],
      },

      {
        path: 'create',
        loadComponent: () =>
          import(
            './features/route-management/create-route/create-route.component'
          ).then((m) => m.CreateRouteComponent),
        canActivate: [RoleGuard(Feature.ROUTE_MANAGEMENT)],
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import(
            './features/route-management/create-route/create-route.component'
          ).then((m) => m.CreateRouteComponent),
        canActivate: [RoleGuard(Feature.ROUTE_MANAGEMENT)],
      },
    ],
  },
  {
    path: 'RateCardManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/rate-card-management/rate-card-management.component'
          ).then((m) => m.RateCardManagementComponent),
        canActivate: [RoleGuard(Feature.RATE_CARD_MANAGEMENT)],
      },
    ],
  },
  {
    path: 'Report',
    children: [
      {
        path: 'trip-report',
        loadComponent: () =>
          import('./features/report-analytics/report-analytics.component').then(
            (m) => m.ReportAnalyticsComponent
          ),
        canActivate: [RoleGuard(Feature.REPORT)],
      },
    ],
  },
  {
    path: 'BillingManagement',
    component: BillingManagementComponent,
    canActivate: [RoleGuard(Feature.BILLING_MANAGEMENT)],
  },
  {
    path: 'BillingManagement/trip-summary',
    component: TripSummaryComponent,
    canActivate: [RoleGuard(Feature.BILLING_MANAGEMENT)],
  },
  {
    path: 'BillingManagement/bill-generation',
    component: BillGenerationComponent,
    canActivate: [RoleGuard(Feature.BILLING_MANAGEMENT)],
  },
  {
    path: 'BillingManagement/generated-bill',
    component: GeneratedBillComponent,
    canActivate: [RoleGuard(Feature.BILLING_MANAGEMENT)],
  },

  {
    path: 'UserManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./auth/components/user-management/user-management.component').then(
            (m) => m.UserManagementComponent
          ),
        canActivate: [AdminGuard],
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import(
            './auth/components/user-onboard/user-onboard.component'
          ).then((m) => m.UserOnboardComponent),
        canActivate: [AdminGuard],
      },
      {
        path: 'onboard/:id',
        loadComponent: () =>
          import(
            './auth/components/user-onboard/user-onboard.component'
          ).then((m) => m.UserOnboardComponent),
        canActivate: [AdminGuard],
      },
    ],
  },
  {
    path: 'transitManagement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/transit-management/transit-management.component'
          ).then((m) => m.TransitManagementComponent),
        canActivate: [RoleGuard(Feature.TRANSIT_MANAGEMENT)],
      },
      {
        path: 'onboard',
        loadComponent: () =>
          import(
            './features/transit-management/transit-onboard/transit-onboard.component'
          ).then((m) => m.TransitOnboardComponent),
        canActivate: [RoleGuard(Feature.TRANSIT_MANAGEMENT)],
      },
      {
        path: 'edit/:id',
        loadComponent: () =>
          import(
            './features/transit-management/transit-onboard/transit-onboard.component'
          ).then((m) => m.TransitOnboardComponent),
        canActivate: [RoleGuard(Feature.TRANSIT_MANAGEMENT)],
      },
      {
        path: 'city-restriction',
        loadComponent: () =>
          import(
            './features/transit-management/city-restriction/city-restriction.component'
          ).then((m) => m.CityRestrictionComponent),
        canActivate: [RoleGuard(Feature.CITY_RESTRICTION)],
      },
    ],
  },
  {
    path: 'tracking',
    loadComponent: () =>
      import('./features/traking/traking.component').then(
        (m) => m.TrackingComponent
      ),
    canActivate: [RoleGuard(Feature.TRACKING)],
  },
  {
    path: 'no-access',
    loadComponent: () =>
      import('./shared/no-access/no-access.component').then(
        (m) => m.NoAccessComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: '',
    canActivate: [() => redirectToFirstAvailableFeature()],
    children: []
  },
];
